import { CertificationRecord, Member, QueryEquipmentQuery, QueryEquipmentQueryVariables } from '__generated__/graphql';
import { EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { difference } from 'lodash';
import { useCallback } from 'react';
import { getFullNameFromMember } from 'utils/stringUtils';
import { useCertificationRecordData } from './useCertificationRecordData';

export interface MemberMissingCerts {
  memberId: string;
  memberName: string;
  missingCerts: string[];
}

export function useCertificationChecker() {
  const client = useGraphQLClient();
  const getCertificationRecords = useCertificationRecordData();

  const getMissingCertifications = useCallback(
    async (memberIds: string[], equipmentIds: string[], members?: Member[]): Promise<MemberMissingCerts[]> => {
      const certificationRecords: CertificationRecord[] = await getCertificationRecords(memberIds);

      const equipmentResult = await client.request<QueryEquipmentQuery, QueryEquipmentQueryVariables>({
        document: EQUIPMENT_QUERY,
        variables: { filter: { id: { contains: equipmentIds } } },
      });

      const requiredCertMap = new Map<string, string>();
      equipmentResult?.equipment?.forEach((eq) => {
        eq.certificationLinks?.forEach((link) => {
          if (link?.certificationId && link.certification?.name) {
            requiredCertMap.set(link.certificationId, link.certification.name);
          }
        });
      });
      const requiredCertIds = Array.from(requiredCertMap.keys());

      const recordsByMember: Record<string, { memberId: string; memberName: string; certIds: Set<string> }> =
        memberIds.reduce(
          (acc, id) => {
            acc[id] = { memberId: id, memberName: '', certIds: new Set<string>() };
            return acc;
          },
          {} as Record<string, { memberId: string; memberName: string; certIds: Set<string> }>
        );
      const nameFromMembers = members
        ? new Map<string, string>(
            members.filter(Boolean).map((m) => [m.id, `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()])
          )
        : undefined;

      for (const record of certificationRecords) {
        const memberId = record.memberId;

        if (!recordsByMember[memberId]) {
          recordsByMember[memberId] = {
            memberId,
            memberName: nameFromMembers?.get(memberId) ?? (record.member ? getFullNameFromMember(record.member) : ''),
            certIds: new Set<string>(),
          };
        } else if (!recordsByMember[memberId].memberName && record.member) {
          recordsByMember[memberId].memberName = getFullNameFromMember(record.member);
        }

        if (record.certificationId) {
          recordsByMember[memberId].certIds.add(record.certificationId);
        }
      }

      const result: MemberMissingCerts[] = memberIds.map((memberId) => {
        const member = recordsByMember[memberId];
        const owned = Array.from(member?.certIds ?? []);
        const missingIds = difference(requiredCertIds, owned);
        const missingNames = missingIds.map((id) => requiredCertMap.get(id) ?? '');
        return {
          memberId: member.memberId,
          memberName: member?.memberName ?? '',
          missingCerts: missingNames,
        };
      });

      return result;
    },
    [client, getCertificationRecords]
  );

  const getRequiredCertificationsFromEquipment = useCallback(
    async (equipmentIds: string[]): Promise<string[]> => {
      if (!equipmentIds.length) return [];

      try {
        const { equipment } = await client.request<QueryEquipmentQuery, QueryEquipmentQueryVariables>({
          document: EQUIPMENT_QUERY,
          variables: {
            filter: { id: { contains: equipmentIds } },
          },
        });

        const certIds =
          equipment?.flatMap((eq) => eq.certificationLinks?.map((link) => link.certificationId)).filter(Boolean) ?? [];

        return Array.from(new Set(certIds));
      } catch (error) {
        console.error('Failed to load certification IDs:', error);
        return [];
      }
    },
    [client]
  );

  return { getMissingCertifications, getRequiredCertificationsFromEquipment };
}
